import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { renderField, renderListView, renderSelectedDataElement } from './fieldRenderers';
import { renderConditionsTable, renderLayoutTable, renderSplitPane, renderExternalFilters, renderBlocksTable } from './tableRenderers';
import { renderHeaderTableConfig, renderTabsConfig, renderSteps, renderSelectedTopLevelDataElement, renderItemsObject } from './specialRenderers';
import { FieldRouter } from './components/FieldRouter';

export const renderNestedObject = (obj, level = 0, skipKeys = [], classes) => {
	if (!obj || typeof obj !== 'object') {
		return <Typography>{String(obj)}</Typography>;
	}

	if (Array.isArray(obj)) {
		return (
			<Box>
				{obj.length === 0 ? (
					<Typography color="textSecondary">(Empty array)</Typography>
				) : (
					obj.map((item, index) => (
						<Box key={index} mb={1}>
							{typeof item === 'object' && item !== null ? (
								renderNestedObject(item, level + 1, skipKeys, classes)
							) : (
								<Typography>{String(item)}</Typography>
							)}
						</Box>
					))
				)}
			</Box>
		);
	}

	const entries = Object.entries(obj);
	if (entries.length === 0) {
		return <Typography color="textSecondary">(Empty object)</Typography>;
	}

	if (level > 2) {
		return (
			<Box className={classes.jsonView}>
				<pre>{JSON.stringify(obj, null, 2)}</pre>
			</Box>
		);
	}

	return (
		<Box>
			{entries
				.filter(([key]) => !skipKeys.includes(key))
				.map(([key, value]) => (
					<Box key={key} mb={1}>
						{Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
									{key}:
								</Typography>
								{value.length === 0 ? (
									<Typography color="textSecondary" style={{ marginLeft: 16 }}>(Empty array)</Typography>
								) : (
									<Box style={{ marginLeft: 16 }}>
										{value.map((item, index) => (
											<Box key={index} mb={1}>
												{typeof item === 'object' && item !== null ? (
													renderNestedObject(item, level + 1, skipKeys, classes)
												) : (
													<Typography>{String(item)}</Typography>
												)}
											</Box>
										))}
									</Box>
								)}
							</Box>
						) : key === 'pagination' && typeof value === 'object' && value !== null && !Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
									{key}:
								</Typography>
								{renderItemsObject(value, classes)}
							</Box>
						) : typeof value === 'object' && value !== null && !Array.isArray(value) && key !== 'splitPane' && key !== 'headerTableConfig' && key !== 'headerTableConditionConfig' && key !== 'tabsConfig' && key !== 'externalFilters' && key !== 'externalFilter' && key !== 'lineItems' && key !== 'poLineItems' && key !== 'summaryItems' && key !== 'steps' && key !== 'compareLineItemsAndPOInfo' && key !== 'pagination' ? (
							<Accordion className={classes.nestedAccordion}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography>
										<strong>{key}</strong>:{' '}
										{Array.isArray(value)
											? `Array (${value.length})`
											: `Object (${Object.keys(value).length} keys)`}
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<FieldRouter fieldKey={key} value={value} classes={classes} showLabel={false} />
									{key !== 'selectedDataElement' && key !== 'selectedTopLevelDataElement' && key !== 'listView' && key !== 'conditions' && key !== 'blocks' && key !== 'externalFilters' && key !== 'externalFilter' && key !== 'layout' && key !== 'splitPane' && key !== 'steps' && key !== 'headerTableConfig' && key !== 'tabsConfig' && key !== 'lineItems' && key !== 'poLineItems' && key !== 'summaryItems' && key !== 'selectedInnerDataElement' && key !== 'compareLineItemsAndPOInfo' && typeof value === 'object' && value !== null ? (
										renderNestedObject(value, level + 1, skipKeys, classes)
									) : null}
								</AccordionDetails>
							</Accordion>
						) : (key === 'externalFilters' || key === 'externalFilter') && Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
									{key}:
								</Typography>
								{renderExternalFilters(value)}
							</Box>
						) : key === 'externalFilter' && typeof value === 'object' && value !== null && !Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
									{key}:
								</Typography>
								{renderItemsObject(value, classes)}
							</Box>
						) : key === 'autoRefresh' && typeof value === 'object' && value !== null && !Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
									{key}:
								</Typography>
								{renderItemsObject(value, classes)}
							</Box>
						) : key === 'compareLineItemsAndPOInfo' && typeof value === 'object' && value !== null && !Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
									{key}:
								</Typography>
								{renderItemsObject(value, classes)}
							</Box>
						) : key === 'headerTableConditionConfig' && typeof value === 'object' && value !== null && !Array.isArray(value) ? (
							<Box>
								<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
									{key}:
								</Typography>
								{renderItemsObject(value, classes)}
							</Box>
						) : (
							<FieldRouter fieldKey={key} value={value} classes={classes} showLabel={true} />
						)}
					</Box>
				))}
		</Box>
	);
};

